import { useState, type FormEvent } from 'react';
import { Alert } from '@/components/alert';
import { Button } from '@/components/button';
import { TextField } from '@/components/text-field';
import type { Copy } from '@/lib/messages';

interface EmailStepProps {
  copy: Copy;
  pending: boolean;
  error: string | null;
  onSubmit: (email: string) => void;
  /** Overrides the button copy where "send code" is not what this step means. */
  submitLabel?: string;
  /**
   * True once a conditional WebAuthn request is listening on this field. It
   * changes nothing visible — the browser draws the passkey row in its own
   * autofill menu — but it is why the input advertises `webauthn`.
   */
  passkeyAutofill?: boolean;
}

export function EmailStep({
  copy,
  pending,
  error,
  onSubmit,
  submitLabel,
  passkeyAutofill = false,
}: EmailStepProps) {
  const [email, setEmail] = useState('');

  function handleSubmit(event: FormEvent) {
    event.preventDefault();
    const trimmed = email.trim();
    if (trimmed) onSubmit(trimmed);
  }

  return (
    <form onSubmit={handleSubmit} noValidate className="space-y-4">
      {error ? <Alert tone="error">{error}</Alert> : null}
      <TextField
        id="email"
        label={copy.emailLabel}
        type="email"
        value={email}
        onChange={(event) => setEmail(event.target.value)}
        // `webauthn` is what puts a saved passkey into the browser's own
        // autofill menu for this field, so a returning user signs in by
        // picking it — no button, no typing, no email at all.
        autoComplete={passkeyAutofill ? 'username webauthn' : 'username'}
        inputMode="email"
        // Phone keyboards otherwise capitalise the first letter of an address.
        autoCapitalize="none"
        autoCorrect="off"
        spellCheck={false}
        placeholder={copy.emailPlaceholder}
        autoFocus
        required
      />
      <Button type="submit" loading={pending} disabled={!email.trim()}>
        {submitLabel ?? copy.emailSubmit}
      </Button>
    </form>
  );
}
